import { api, API_ENDPOINTS } from './api';

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
  timestamp: string;
}

// ── Spec 018 types ─────────────────────────────────────────────────────────

export interface ChildSchoolProfileSummary {
  childId: number;
  firstName: string;
  lastName: string;
  countryId: number | null;
  countryName: string | null;
  councilId: number | null;
  councilName: string | null;
  schoolName: string | null;
  gradeLevel: string | null;
  schoolType: 'PRIMARY' | 'SECONDARY' | null;
  /** True when an unknown_location_reports row with status=PENDING exists for this child (spec 021). */
  isPendingValidation: boolean;
}

export interface CourseCard {
  courseId: number;
  name: string;
  courseType: string | null;
  subject: string | null;
  curriculumLevel: string | null;
  description: string | null;
  imageUrl: string | null;
}

export interface AbacusRegistrationTileCourse {
  id: number;
  name: string;
  registrationMethod: string;
}

export interface AbacusRegistrationTileRef {
  id: number;
  name: string;
}

export interface AbacusRegistrationTile {
  variant: 'JUNIOR' | 'SENIOR';
  label: string;
  courses: AbacusRegistrationTileCourse[];
  comingSoonTeaser?: AbacusRegistrationTileRef | null;
  enrolledCourse?: AbacusRegistrationTileRef | null;
}

export interface AbacusAvailableTilesResponse {
  tiles: AbacusRegistrationTile[];
}

export interface ChildAvailableCoursesResponse {
  childProfile: ChildSchoolProfileSummary;
  courses: CourseCard[];
  profileComplete: boolean;
  serviceUnavailable: boolean;
  noCoursesFound: boolean;
  abacusTiles?: AbacusAvailableTilesResponse | null;
}

export interface EnrollmentFailure {
  courseId: number;
  reason: string;
}

export interface BatchEnrollResponse {
  enrolledCourseIds: number[];
  failures: EnrollmentFailure[];
}

export interface ChildSchoolProfileUpdateRequest {
  countryId: number | null;
  councilId: number | null;
  schoolName: string;
  gradeLevel: string;
  councilName?: string;
  unknownSchool?: boolean;
  unknownCouncilName?: string;
}

// ── Legacy types ───────────────────────────────────────────────────────────

export interface OpenCourse {
  id: number;
  name: string;
  description?: string;
  courseType?: string;
  board?: string;
  curriculumSubjectId?: number;
  curriculumId?: number;
  registrationMethod?: string;
  durationWeeks?: number;
}

export interface CourseSearchResult {
  courses: OpenCourse[];
  childEnrolledCourseIds: number[];
  availableBoards: string[];
  availableCurriculumSubjectIds: number[];
  availableCurriculumIds: number[];
}

export interface EnrollChildRequest {
  childId: number;
  courseId: number;
}

class ParentCourseService {
  async searchCourses(
    childId: number,
    board?: string,
    curriculumSubjectId?: number,
    curriculumId?: number,
  ): Promise<CourseSearchResult> {
    const params = new URLSearchParams({ childId: childId.toString() });
    if (board) params.set('board', board);
    if (curriculumSubjectId != null) params.set('curriculumSubjectId', curriculumSubjectId.toString());
    if (curriculumId != null) params.set('curriculumId', curriculumId.toString());

    const response = await api.get<ApiResponse<CourseSearchResult>>(
      `${API_ENDPOINTS.PARENT_COURSES.SEARCH}?${params.toString()}`,
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to search courses');
  }

  async enrollChildInCourse(childId: number, courseId: number): Promise<void> {
    const response = await api.post<ApiResponse<unknown>>(
      API_ENDPOINTS.PARENT_COURSES.ENROLL_CHILD,
      { childId, courseId } satisfies EnrollChildRequest,
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to enroll child');
    }
  }

  async getChildEnrolledCourseIds(childId: number): Promise<number[]> {
    const response = await api.get<ApiResponse<number[]>>(
      `${API_ENDPOINTS.PARENT_COURSES.CHILD_ENROLLED_IDS}/${childId}/enrolled-ids`,
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to get enrolled course IDs');
  }

  async getAvailableCourses(childId: number): Promise<ChildAvailableCoursesResponse> {
    const url = API_ENDPOINTS.PARENT_COURSES.AVAILABLE.replace('{childId}', childId.toString());
    const response = await api.get<ApiResponse<ChildAvailableCoursesResponse>>(url);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to load available courses');
  }

  async enrollBatch(childId: number, courseIds: number[]): Promise<BatchEnrollResponse> {
    const url = API_ENDPOINTS.PARENT_COURSES.ENROLL_BATCH.replace('{childId}', childId.toString());
    const response = await api.post<ApiResponse<BatchEnrollResponse>>(url, { courseIds });
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Enrollment failed');
  }

  async updateSchoolProfile(
    childId: number,
    request: ChildSchoolProfileUpdateRequest,
  ): Promise<ChildSchoolProfileSummary> {
    const url = API_ENDPOINTS.PARENT_COURSES.UPDATE_SCHOOL_PROFILE.replace(
      '{childId}',
      childId.toString(),
    );
    const response = await api.patch<ApiResponse<ChildSchoolProfileSummary>>(url, request);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to update school profile');
  }
}

export const parentCourseService = new ParentCourseService();
